/**
 * useTheme.js — the one place the colour theme lives.
 *
 * THE DEFECT THIS FIXES
 * The theme was implemented twice: `components/layout/AppShell.js` held it in
 * component state, and `features/settings/AppearanceSection.js` wrote the same
 * `trudesk-theme` localStorage key and the same `data-theme` attribute from its own
 * copy. Neither observed the other, so saving a theme in Settings left the
 * topbar toggle holding the old value — its next click wrote the theme the user
 * had just replaced — and the two only agreed again after a full page reload.
 *
 * There is now one module-level value, one setter, and a subscription every
 * consumer shares. `storage` events are listened for as well, so a second tab
 * follows along.
 *
 * The preference is per device, not per tenant: it is stored in localStorage
 * and never in Firestore, because a shared login must not force one colour
 * scheme on everybody.
 */

import { useCallback, useEffect, useState } from 'react';

export const THEME_KEY = 'trudesk-theme';

export const THEME_OPTIONS = [
  { value: 'system', label: 'Match my system' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const VALID = THEME_OPTIONS.map((option) => option.value);

export function isTheme(value) {
  return VALID.includes(value);
}

/** The stored preference, or 'system' when there is none (or storage throws). */
export function readStoredTheme() {
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    return isTheme(stored) ? stored : 'system';
  } catch {
    // Private mode can make localStorage throw on read as well as on write.
    return 'system';
  }
}

/* --- the shared value --------------------------------------------- */

let current = readStoredTheme();
const listeners = new Set();

function publish(theme) {
  current = theme;
  listeners.forEach((listener) => listener(theme));
}

/** 'system' means "follow the OS", which is the absence of the attribute. */
function paint(theme) {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

/**
 * Apply a theme everywhere and persist it.
 *
 * The document and every subscriber are updated BEFORE the write, so a browser
 * that refuses to store the preference still shows it for this session — the
 * throw then tells the caller that only the persistence failed.
 *
 * @throws whatever localStorage throws when it refuses the write
 */
export function applyTheme(value) {
  const theme = isTheme(value) ? value : 'system';
  paint(theme);
  publish(theme);
  window.localStorage.setItem(THEME_KEY, theme);
}

/** Subscribe to theme changes. Returns an unsubscribe function. */
export function subscribeToTheme(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/* Another tab changing the preference is a change here too. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== THEME_KEY) return;
    const theme = isTheme(event.newValue) ? event.newValue : 'system';
    if (theme === current) return;
    paint(theme);
    publish(theme);
  });
}

/* --- the hook ------------------------------------------------------ */

/**
 * @returns {[string, Function]} the current theme and a setter that never
 *          throws — it returns true when the preference was also persisted.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(current);

  useEffect(() => {
    // Paint on mount so a stored preference survives a reload even if this is
    // the first consumer to run.
    paint(current);
    setThemeState(current);
    return subscribeToTheme(setThemeState);
  }, []);

  const setTheme = useCallback((next) => {
    try {
      applyTheme(next);
      return true;
    } catch {
      /* Storage is unavailable; the theme still applies for this session. */
      return false;
    }
  }, []);

  return [theme, setTheme];
}

export default useTheme;
